
import { useEffect, useState } from 'react';
import { trackEvent } from '@/utils/analytics';

interface UseReadingProgressOptions {
  trackMilestones?: boolean;
  milestones?: number[];
  postTitle?: string;
  postId?: string;
}

const useReadingProgress = ({
  trackMilestones = true,
  milestones = [25, 50, 75, 100],
  postTitle,
  postId
}: UseReadingProgressOptions = {}) => {
  const [readingProgress, setReadingProgress] = useState(0);
  const [trackedMilestones, setTrackedMilestones] = useState<number[]>([]);

  // Add reading progress tracking
  useEffect(() => {
    // Function to calculate and update reading progress
    const updateReadingProgress = () => {
      const currentProgress = window.scrollY;
      const scrollHeight = document.body.scrollHeight - window.innerHeight;
      if (scrollHeight) {
        setReadingProgress(Number((currentProgress / scrollHeight).toFixed(2)) * 100);
      }
    };

    // Add scroll listener to update progress
    window.addEventListener('scroll', updateReadingProgress);
    
    // Clean up listener when component unmounts
    return () => {
      window.removeEventListener('scroll', updateReadingProgress);
    };
  }, []);

  // Track reading milestones
  useEffect(() => {
    if (!trackMilestones || !postTitle) return;

    const trackMilestone = (milestone: number) => {
      const numericId = postId ? Number(postId) || undefined : undefined;
      trackEvent('Blog', 'ReadingMilestone', `Post: ${postTitle}, Milestone: ${milestone}%`, milestone);
    };
    
    const checkMilestones = () => {
      milestones.forEach(milestone => {
        if (readingProgress >= milestone && !trackedMilestones.includes(milestone)) {
          setTrackedMilestones(prev => [...prev, milestone]);
          trackMilestone(milestone);
        }
      });
    };
    
    checkMilestones();
  }, [readingProgress, postTitle, postId, trackMilestones, milestones, trackedMilestones]);

  return readingProgress;
};

export default useReadingProgress;
